import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography, CustomTypographyProps } from '../Typography';
export type TypographyWithOverflowHandlerProps = {
  /**
   * The text to display with middle truncation when it overflows
   */
  text: string;
  /**
   * Optional props to pass to the underlying Typography component
   */
  typographyProps?: Omit<CustomTypographyProps, 'children'>;
};
const StyledTypography = styled(Typography)(() => ({
  display: 'flex',
  flexDirection: 'row',
  '&::before, &::after': {
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    flex: '0 1 auto',
    minWidth: 0,
    maxWidth: '50%',
  },
  '&::before': {
    content: 'attr(data-content-start)',
    textOverflow: 'ellipsis',
  },
  '&::after': {
    content: 'attr(data-content-end)',
    textOverflow: 'clip',
    direction: 'rtl',
    unicodeBidi: 'bidi-override',
  },
}));
// Swap mirrored bracket characters to maintain visual correctness when reversed for RTL
const mirrorChars: Record<string, string> = {
  '(': ')',
  ')': '(',
  '[': ']',
  ']': '[',
  '{': '}',
  '}': '{',
  '<': '>',
  '>': '<',
};
/**
 * Reverses a string and mirrors bracket characters for correct RTL display
 */
const reverseWithMirror = (str: string): string =>
  str
    .split('')
    .reverse()
    .map((char) => {
      return mirrorChars[char] || char;
    })
    .join('');
/**
 * A typography component that handles text overflow by truncating the text
 * and showing ellipses in the middle part of the text.
 *
 * This is useful for displaying file paths, URLs, or other long strings
 * where both the beginning and end are important context.
 */
export const TypographyWithOverflowHandler: React.FC<TypographyWithOverflowHandlerProps> = ({
  text: baseText,
  typographyProps,
}) => {
  // If the text contains spaces, replace them with non-breaking spaces so they are not collapsed in RTL and not removed if the are at the beginning or end of the text
  const text = baseText.replace(/ /g, '\u00A0');
  // Handle empty or very short text - no need for truncation logic
  if (!text || text.length <= 1) {
    return <Typography {...typographyProps}>{text || ''}</Typography>;
  }
  const middleIndex = Math.ceil(text.length / 2);
  const firstPart = text.slice(0, middleIndex);
  // Reverse the second part and mirror brackets so RTL displays correctly while truncating from the left
  const secondPart = reverseWithMirror(text.slice(middleIndex));
  return <StyledTypography {...typographyProps} data-content-start={firstPart} data-content-end={secondPart} />;
};
